import { Component, input, signal, Output, EventEmitter, inject, computed, linkedSignal, ChangeDetectionStrategy } from "@angular/core";
import { ReportRunState, ReportStore } from "../../public-api";
import { ReportRunner } from "../../services/report-runner";
import { Report } from "../../models/report";
import { RisrForm, RisrFormRenderer, ServerConfig } from "@fry-it/risr-core";
import { MatButtonModule } from "@angular/material/button";
import { ReportResult } from "../report-run/report-result/report-result";

interface ReportModel {
    reportData: any;
    reportResult: any;
    reportId: string;
    generatedAt: string;
}

@Component({
    selector: 'kz-report-field',
    templateUrl: './report-field.html',
    imports: [
        MatButtonModule,
        RisrFormRenderer,
        ReportResult
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        ReportRunState
    ]
})
export class ReportFieldComponent {
    model = input.required<ReportModel>()
    extra = input.required<any>()
    reportId = input.required<string>()
    readOnly = input.required<boolean>()

    resultModel = linkedSignal<any>(() => this.model());

    // TODO: Do I need this?
    report = input.required<any>()
    $error = signal<Error | null>(null);

    $currentReport = signal<Report | undefined>(undefined);
    $reportForm = signal<RisrForm | undefined>(undefined);
    $currentUser = computed(() => this.extra().user);

    @Output() update = new EventEmitter<any>();

    private serverConfig = inject(ServerConfig);
    private readonly reportStore = inject(ReportStore);
    private readonly reportRunner = inject(ReportRunner);
    private readonly reportRunState = inject(ReportRunState);
    private readonly options = {
        showType: 'embedded',
        forceOutputOptions: {
            noPagination: true,
            noRowCount: false,
            showCsvDownload: false,
            size: this.serverConfig.reports.embeddedSize
        }
    }

    readonly $mode = signal<'empty' | 'loading' | 'generating' | 'ready' | 'error'>(
        'empty'
    )

    async ngOnInit() {
        const model = this.model();
        const result = model.reportResult;
        if (result && model.reportId) {
            await this.prepareView();
        } else {
            await this.prepareReport();
        }
    }

    async prepareReport() {
        try {
            this.$mode.set('loading');
            await this.loadReportForm();
            this.$mode.set('generating');
        }
        catch (err) {
            this.$error.set(err instanceof Error ? err : new Error(String(err)));
            this.$mode.set('error');
        }
    }

    async prepareView() {
        try {
            this.$mode.set('loading');
            this.$error.set(null);
            const data = this.model();
            const report = await this.reportStore.get(data.reportId);
            this.$currentReport.set(report);
            this.reportRunState.initializeWithResult(report.id, report.report.display, data.reportData, this.options, this.$currentUser(), data.reportResult);
            this.$mode.set('ready');
        } catch (err) {
            this.$error.set(err instanceof Error ? err : new Error(String(err)));
            this.$mode.set('error');
        }
    }

    async generate() {
        if (this.readOnly()) {
            return;
        }
        const form = this.$reportForm();
        if (!form) {
            return;
        }
        if (!form.valid) {
            form.markAllAsTouched();
            return;
        }
        const currentReport = this.$currentReport();
        if (!currentReport) {
            return;
        }
        const model = form.fullValue();
        try {
            this.$mode.set('loading');
            this.$error.set(null);
            this.reportRunState.initialize(currentReport.id, currentReport.report.display, model, this.options, this.$currentUser());
            const result = await this.reportRunState.run();
            this.resultModel.update((value) => {
                return {
                    ...value,
                    reportData: model,
                    reportResult: result,
                    reportId: currentReport.id,
                    generatedAt: new Date().toISOString()
                };
            });
            this.update.emit(this.resultModel());
            this.$mode.set('ready');
        } catch (err) {
            this.$error.set(err instanceof Error ? err : new Error(String(err)));
            this.$mode.set('error');
        }
    }

    async regenerate() {
        this.resultModel.set({})
        this.update.emit(this.resultModel());
        await this.prepareReport();
    }

    private async loadReportForm(): Promise<void> {
        const model = this.model();
        const report = await this.reportStore.getByGroup(this.reportId());
        if (report.state !== 'published') {
            throw new Error("Report no longer available");
        }
        this.$currentReport.set(report);
        const form = await this.reportRunner.getForm(report.report.filterForm, model.reportData || {});
        this.$reportForm.set(form);
    }

}
